import { useState, useEffect, useRef, useMemo, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Hero from "../components/landing/Hero";
import FeatureShowcaseAnimation from "../components/landing/FeatureShowcaseAnimation";
import { lazyWithRetry } from "../utils/lazyWithRetry";

const HowItWorks = lazyWithRetry(() => import("../components/landing/HowItWorks"));
const UseCases = lazyWithRetry(() => import("../components/landing/UseCases"));
import Footer from "../components/landing/Footer";
import LandingNavbar from "../components/landing/LandingNavbar";
import JsonLd from "../components/seo/JsonLd";
import FAQSection from "../components/seo/FAQSection";
import {
    buildOrganizationSchema,
    buildSoftwareApplicationSchema,
    buildWebSiteSchema,
    buildHomePageWebPageSchema,
    buildHomeQuickStartHowToSchema,
    buildHomeBreadcrumbSchema,
    buildHomeComparisonItemListSchema,
} from "../data/seo/structuredData";
import type { FaqItem } from "../components/seo/FAQSection";
import useManagedSupportPricing from "../hooks/useManagedSupportPricing";
import usePageSeo from "../hooks/usePageSeo";
// Custom SVG icons to replace Heroicons
const CheckIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
);

// Animated showcases
const SectionHeading = ({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) => (
    <div className="max-w-3xl">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
            <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
            {eyebrow}
        </div>
        <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">{title}</h2>
        <p className="mt-4 text-slate-300 leading-relaxed">{description}</p>
    </div>
);

const MockCode = ({ lines }: { lines: string[] }) => (
    <div className="rounded-xl border border-slate-800 bg-slate-900/70 backdrop-blur p-4 text-sm text-slate-300 font-mono">
        {lines.map((l, i) => (
            <div key={i} className="flex items-start gap-3">
                <span className="select-none text-slate-600 w-6 text-right">{i + 1}</span>
                <span className="whitespace-pre-wrap">{l}</span>
            </div>
        ))}
    </div>
);

const KnowledgeBaseSection = () => (
    <section id="knowledge-base" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30">
            <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-pink-500/30 blur-3xl" />
            <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <SectionHeading
                        eyebrow="Knowledge Base"
                        title="Your content, instantly crawlable and query-ready"
                        description="Connect websites, PDFs, and docs. We crawl, chunk, embed, and keep everything fresh with scheduled recrawls. Search is semantic, responses are grounded in your content, and citations link back to the source."
                    />
                    <ul className="mt-8 space-y-3 text-slate-300">
                        <li className="flex items-start gap-3"><CheckIcon />
                            <span><strong>Multi-source ingestion</strong> — websites, sitemaps, PDFs, DOCX, and FAQ sheets.</span>
                        </li>
                        <li className="flex items-start gap-3"><CheckIcon />
                            <span><strong>Automatic chunking & embeddings</strong> — tuned for high recall with low token cost.</span>
                        </li>
                        <li className="flex items-start gap-3"><CheckIcon />
                            <span><strong>Live sync</strong> — scheduled crawls and delta updates keep answers current.</span>
                        </li>
                        <li className="flex items-start gap-3"><CheckIcon />
                            <span><strong>Grounded answers</strong> — citations and source URLs for every response.</span>
                        </li>
                    </ul>
                    <div className="mt-8">
                        <Link
                            to="/ai-knowledge-base"
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                        >
                            Learn more about Knowledge Base
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </Link>
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                    className="relative"
                >
                    <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-orange-500/20 to-pink-500/20 blur-xl" />
                    <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                        <div className="flex items-center gap-2 text-xs text-slate-400">
                            <div className="h-2 w-2 rounded-full bg-emerald-400" /> Live crawl
                        </div>
                        <div className="mt-4 grid grid-cols-1 gap-4">
                            <MockCode lines={[
                                "POST /knowledge/crawl",
                                "payload:",
                                "  url: 'https://docs.example.com'",
                                "  include: ['/*.md', '/guides/*']",
                                "  schedule: '0 3 * * *'  # daily at 3am",
                            ]} />
                            <MockCode lines={[
                                "Answer: How do I integrate?",
                                "- Uses OAuth 2.0",
                                "- Create a Client ID in dashboard",
                                "- Add redirect URL",
                                "[source] https://docs.example.com/guide#oauth",
                            ]} />
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

const WorkflowBuilderSection = () => (
    <section id="workflow" className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <SectionHeading
                        eyebrow="Workflow Builder"
                        title="Design conversations like a flowchart, publish with one click"
                        description="Drag to connect nodes, add conditions, test execution paths, and publish versions safely. Dynamic node configs are fetched from backend schemas for type-safe editing."
                    />
                    <div className="mt-8 grid grid-cols-2 gap-4">
                        {[
                            "Drag-to-connect",
                            "Branching & guards",
                            "Realtime previews",
                            "Versioning",
                        ].map((t) => (
                            <div key={t} className="flex items-center gap-2 text-slate-300"><CheckIcon /><span>{t}</span></div>
                        ))}
                    </div>
                    <div className="mt-8">
                        <Link
                            to="/ai-workflow-builder"
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                        >
                            Learn more about Workflow Builder
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </Link>
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="relative">
                        <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-indigo-500/20 to-purple-500/20 blur-xl" />
                        <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                            <div className="grid grid-cols-3 gap-4">
                                {[
                                    { name: "Start", color: "from-emerald-500 to-teal-500" },
                                    { name: "LLM", color: "from-indigo-500 to-purple-500" },
                                    { name: "KB Search", color: "from-orange-500 to-pink-500" },
                                    { name: "Condition", color: "from-yellow-500 to-orange-500" },
                                    { name: "Function", color: "from-blue-500 to-cyan-500" },
                                    { name: "End", color: "from-slate-500 to-slate-700" },
                                ].map((n, idx) => (
                                    <motion.div
                                        key={n.name}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.05 * idx }}
                                        viewport={{ once: true }}
                                        className={`rounded-xl border border-slate-800 bg-gradient-to-r ${n.color} p-4 text-center text-sm font-semibold text-white shadow-lg`}
                                    >
                                        {n.name}
                                    </motion.div>
                                ))}
                            </div>
                            <div className="mt-6">
                                <MockCode lines={[
                                    "if (intent == 'pricing') -> node:SendPricing",
                                    "elif (score > 0.7) -> node:AnswerFromKB",
                                    "else -> node:EscalateToHuman",
                                ]} />
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

const VideoShowcaseSection = () => (
    <section id="see-in-action" className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute -top-24 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-orange-500/20 blur-3xl" />
            <div className="absolute right-0 top-24 h-96 w-96 rounded-full bg-pink-500/10 blur-3xl" />
            <div className="absolute left-0 bottom-0 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />
        </div>
        <div className="mx-auto max-w-5xl px-6 lg:px-8 py-16 lg:py-24">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true, amount: 0.2 }}
                className="text-center"
            >
                <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                    <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                    See it in action
                </div>
                <h2 className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white">
                    Everything FloGPT does, in 20 seconds
                </h2>
                <p className="mx-auto mt-4 max-w-2xl text-slate-300 leading-relaxed">
                    Embed a chat widget, run AI voice agents on a phone number,
                    ground answers in your knowledge base, design pipelines visually,
                    trigger outbound calls from any webhook — and route every
                    conversation into a unified CRM inbox with AI-to-human handover.
                </p>
                <div className="mt-10 relative">
                    <div className="absolute -inset-2 rounded-3xl bg-gradient-to-r from-orange-500/25 to-pink-500/25 blur-xl" />
                    <div className="relative overflow-hidden rounded-3xl border border-slate-700/80 bg-slate-900/80 shadow-2xl">
                        <FeatureShowcaseAnimation />
                    </div>
                </div>
            </motion.div>
        </div>
    </section>
);

const VoiceAgentsSection = () => (
    <section id="voice-agents" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30">
            <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-emerald-500/30 blur-3xl" />
            <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-orange-500/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="relative">
                        <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-orange-500/20 blur-xl" />
                        <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-semibold">
                                        A
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold">Aria · AI Voice Agent</div>
                                        <div className="text-xs text-slate-400">Acme Dental · +1 (555) 010-2025</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-emerald-300">
                                    <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                    Live · 00:42
                                </div>
                            </div>
                            <div className="mt-5 space-y-2">
                                {[
                                    { role: "AI", text: "Thanks for calling Acme Dental. How can I help?" },
                                    { role: "Caller", text: "I'd like a cleaning Friday afternoon." },
                                    { role: "AI", text: "I have 2:30pm or 4:15pm. Which works?" },
                                    { role: "Caller", text: "4:15 please." },
                                    { role: "AI", text: "Booked. Confirmation SMS on its way." },
                                ].map((m, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 6 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.05 * i }}
                                        viewport={{ once: true }}
                                        className={`w-fit max-w-[85%] rounded-2xl px-3 py-1.5 text-xs shadow ${
                                            m.role === "AI"
                                                ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-white"
                                                : "bg-slate-800/80 text-slate-100 ml-auto"
                                        }`}
                                    >
                                        <span className="opacity-70 mr-1.5 text-[10px] uppercase tracking-wider">{m.role}</span>
                                        {m.text}
                                    </motion.div>
                                ))}
                            </div>
                            <div className="mt-5 pt-4 border-t border-slate-800 flex items-center gap-2 text-[11px] text-slate-400 flex-wrap">
                                <span className="opacity-70">Pipeline:</span>
                                {["Greet", "Listen", "Book", "SMS"].map((step, i, arr) => (
                                    <span key={step} className="flex items-center gap-1.5">
                                        <span className="rounded-md bg-slate-800 px-2 py-0.5 text-slate-200">{step}</span>
                                        {i < arr.length - 1 && <span className="text-slate-600">→</span>}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <SectionHeading
                        eyebrow="Voice Agents · Call Pipeline"
                        title="AI agents that pick up calls — and dial out — 24/7"
                        description="Connect a phone number, design the call flow visually with the Call Pipeline editor, and let the agent handle support, bookings, qualification and outbound campaigns. Multilingual, with smart provider routing for India + global."
                    />
                    <ul className="mt-8 space-y-3 text-slate-300">
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Inbound + <Link to="/ai-outbound-triggers" className="text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:text-orange-300">outbound</Link></strong> — answers calls and dials out from webhooks, <Link to="/shopify-ai-customer-support" className="text-orange-400 underline decoration-orange-400/40 underline-offset-2 hover:text-orange-300">Shopify events</Link>, scheduled lists, time-relative cadences and more.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Visual Call Pipeline</strong> — drag nodes, branch on intent, call tools, transfer to humans.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>16 languages</strong> — English (US/UK/IN/AU/CA/IE/NZ), Hindi, Tamil, Telugu, Kannada, Malayalam, Marathi, Bengali, Gujarati, Punjabi.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Smart routing</strong> — a managed number or your own Twilio account for global, Jio & MSG91 for India (DLT-compliant).</span></li>
                    </ul>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link
                            to="/ai-voice-agents"
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                        >
                            Explore Voice Agents
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </Link>
                        <Link
                            to="/ai-voice-agents#call-pipeline"
                            className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white backdrop-blur transition hover:bg-white/10"
                        >
                            See the Call Pipeline
                        </Link>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

const CRMSection = () => (
    <section id="crm" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30">
            <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-cyan-500/30 blur-3xl" />
            <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <SectionHeading
                        eyebrow="CRM · Unified Inbox"
                        title="Every conversation, one ticket queue, one contact"
                        description="Chat, voice, email, SMS, WhatsApp, Instagram and Facebook all land in the same inbox — with identity resolved, priority set, and the AI's transcript already attached. Your team picks up where the agent left off."
                    />
                    <ul className="mt-8 space-y-3 text-slate-300">
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>7 channels unified</strong> — chat, call, email, SMS, WhatsApp, Instagram, Facebook plus manual tickets.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Contact identity resolution</strong> — same person, one timeline, across every channel and identifier.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>AI-to-human handover</strong> — escalations open a ticket with the full transcript and routed assignment.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Role-scoped analytics</strong> — Self, Agent and Supervisor dashboards with SLA + AI deflection.</span></li>
                    </ul>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link
                            to="/ai-crm"
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                        >
                            Explore the AI CRM
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </Link>
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                    className="relative"
                >
                    <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-cyan-500/20 to-blue-500/20 blur-xl" />
                    <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <div className="text-white font-semibold">Support Inbox</div>
                                <div className="text-xs text-slate-400">12 open · 3 unassigned · SLA on track</div>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-emerald-300">
                                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                Live
                            </div>
                        </div>
                        <div className="mt-5 space-y-2">
                            {[
                                { ch: "WhatsApp", from: "Priya · +91 98...", subj: "Where is my order #1042?", prio: "high", color: "from-green-500 to-emerald-500" },
                                { ch: "Voice", from: "Inbound · +1 (555) 010-2025", subj: "Booking · Friday 4:15pm", prio: "medium", color: "from-orange-500 to-pink-500" },
                                { ch: "Email", from: "alex@acme.com", subj: "Refund request — order #998", prio: "urgent", color: "from-red-500 to-rose-500" },
                                { ch: "Chat", from: "Anonymous · India", subj: "Does this ship to Mumbai?", prio: "low", color: "from-cyan-500 to-blue-500" },
                            ].map((t, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 6 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.05 * i }}
                                    viewport={{ once: true }}
                                    className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/70 p-3"
                                >
                                    <span className={`rounded-md bg-gradient-to-r ${t.color} px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white shadow`}>
                                        {t.ch}
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-[12px] text-slate-400 truncate">{t.from}</div>
                                        <div className="text-[13px] text-white truncate font-medium">{t.subj}</div>
                                    </div>
                                    <span
                                        className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                                            t.prio === "urgent"
                                                ? "bg-red-500/15 text-red-300"
                                                : t.prio === "high"
                                                ? "bg-orange-500/15 text-orange-300"
                                                : t.prio === "medium"
                                                ? "bg-yellow-500/15 text-yellow-300"
                                                : "bg-slate-500/15 text-slate-300"
                                        }`}
                                    >
                                        {t.prio}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                        <div className="mt-4 pt-4 border-t border-slate-800 flex items-center justify-between text-[11px] text-slate-400">
                            <span>Auto-assigned · Aria · AI · 0.2s</span>
                            <span>Handover ready</span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

const ChatWidgetSection = () => (
    <section id="chat-widget" className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <div className="relative">
                        <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-teal-500/20 to-emerald-500/20 blur-xl" />
                        <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden">
                                    <img
                                        src="/images/flogpt-iso.png"
                                        alt="FloGPT"
                                        width={48}
                                        height={48}
                                        loading="lazy"
                                        decoding="async"
                                        className="h-full w-full object-contain"
                                    />
                                </div>
                                <div className="text-white font-semibold">FloGPT Widget</div>
                            </div>
                            <div className="mt-4 space-y-3">
                                {[
                                    { role: "User", text: "Do you support multi-language?" },
                                    { role: "AI", text: "Yes! We support 50+ languages with automatic detection." },
                                    { role: "User", text: "How can I embed this widget?" },
                                    { role: "AI", text: "Paste a single script tag or use NPM for React/Next.js." },
                                ].map((m, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 8 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.05 * i }}
                                        viewport={{ once: true }}
                                        className={`w-fit max-w-md rounded-2xl px-4 py-2 text-sm ${m.role === 'AI' ? 'bg-slate-800/80 text-slate-100' : 'bg-gradient-to-r from-orange-500 to-pink-500 text-white'} shadow`}
                                    >
                                        <span className="font-semibold mr-2">{m.role}:</span>{m.text}
                                    </motion.div>
                                ))}
                            </div>
                            <div className="mt-6">
                                <MockCode lines={[
                                    "<script src=\"https://cdn.flogpt.com/widget.js\"></script>",
                                    "<script>",
                                    "  FloGPT.init({",
                                    "    widgetId: 'YOUR_ID',",
                                    "    primaryColor: '#f97316',",
                                    "    launcherPosition: 'bottom-right'",
                                    "  })",
                                    "</script>",
                                ]} />
                            </div>
                        </div>
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <SectionHeading
                        eyebrow="Chat Widget"
                        title="Beautiful, brandable, and fast — embed in minutes"
                        description="Customize colors, avatars, behavior, and escalation rules. Use file uploads, rich cards, and actions. Our widget streams tokens for sub-200ms perceived latency."
                    />
                    <ul className="mt-8 space-y-3 text-slate-300">
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Quick install</strong> — script tag or React package.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Fully brandable</strong> — theming, launcher styles, custom prompts.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Omnichannel</strong> — the same agent answers on web, WhatsApp, Slack, Instagram and more.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Handover</strong> — escalate to humans with context.</span></li>
                    </ul>
                    <div className="mt-8">
                        <Link
                            to="/ai-chat-widget"
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                        >
                            {import.meta.env.VITE_SITE_REGION === "in" ? "Learn more about Chat Widget" : "AI chat widget"}
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </Link>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);

/**
 * Answer-first definitional block. AEO note: the wrapper id (`what-is-flogpt`)
 * and the `flogpt-tldr` aside are referenced verbatim by the WebPage
 * `speakable.cssSelector` in `structuredData.ts`. AI engines (Perplexity,
 * ChatGPT, Gemini) extract the first paragraph for "What is FloGPT?" answers,
 * so the prose is intentionally definitional and quotable rather than
 * marketing-flavoured.
 */
const WhatIsFloGPTSection = () => (
    <section
        id="what-is-flogpt"
        aria-labelledby="what-is-flogpt-heading"
        className="relative overflow-hidden border-y border-slate-800/60 bg-slate-950/40"
    >
        <div className="mx-auto max-w-6xl px-6 lg:px-8 py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-12">
                <div className="lg:col-span-2">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                        What is FloGPT?
                    </div>
                    <h2
                        id="what-is-flogpt-heading"
                        className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white leading-tight"
                    >
                        FloGPT is an AI Chat &amp; Voice Agents platform with a built-in AI CRM.
                    </h2>
                    <p className="mt-5 text-lg text-slate-300 leading-relaxed">
                        Companies use FloGPT to embed an AI chat widget on any website, run AI
                        voice agents that answer and place phone calls 24/7, and unify chat, voice,
                        email, SMS, WhatsApp, Instagram and Facebook conversations into one ticket
                        inbox &mdash; all grounded in their own knowledge base.
                    </p>
                    <p className="mt-4 text-base text-slate-400 leading-relaxed">
                        FloGPT replaces the stack you would normally assemble from a chat-bot
                        vendor, a voice-AI vendor, a help-desk CRM, and a workflow tool. The same
                        agent intelligence and knowledge base power every surface, so deflection
                        rates, brand voice, and SLA tracking stay consistent across channels.
                    </p>
                </div>
                <aside
                    id="flogpt-tldr"
                    aria-label="FloGPT at a glance"
                    className="rounded-2xl border border-slate-800 bg-slate-900/70 p-6 text-sm text-slate-300 shadow-lg shadow-black/10"
                >
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        At a glance
                    </h3>
                    <dl className="mt-4 space-y-3">
                        <div>
                            <dt className="text-slate-400">Category</dt>
                            <dd className="text-white font-medium">
                                Conversational AI · Customer Support
                            </dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Channels</dt>
                            <dd className="text-white font-medium">
                                Chat, voice, email, SMS, WhatsApp, Instagram, Facebook
                            </dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Languages</dt>
                            <dd className="text-white font-medium">
                                50+ chat · 16 voice (incl. 9 Indic)
                            </dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Setup time</dt>
                            <dd className="text-white font-medium">
                                &lt; 30 min for chat · &lt; 1 hour for voice
                            </dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Pricing</dt>
                            <dd className="text-white font-medium">
                                Free ($0, 100 messages/month) · paid from $49/month
                            </dd>
                        </div>
                        <div>
                            <dt className="text-slate-400">Integrations</dt>
                            <dd className="text-white font-medium">
                                Shopify, Twilio, Jio, MSG91, Google &amp; Outlook calendars
                            </dd>
                        </div>
                    </dl>
                </aside>
            </div>
        </div>
    </section>
);

/**
 * On-page mirror of the `HowTo` JSON-LD in `structuredData.ts`. Step text
 * matches the schema 1:1 so AI engines can quote either source. Wrapper id
 * is referenced by the WebPage `speakable.cssSelector`.
 */
const QuickStartSection = () => {
    const steps: { n: string; title: string; body: string; accent: string }[] = [
        {
            n: "1",
            title: "Connect your knowledge base",
            body: "Sign up and point the crawler at your website, sitemap, PDFs, or FAQ docs. FloGPT chunks, embeds and indexes the content automatically. Schedule recurring re-crawls so answers stay current.",
            accent: "from-emerald-500 to-teal-500",
        },
        {
            n: "2",
            title: "Pick a vertical template",
            body: "Choose Auto Dealers, Dental, Education, Fitness, Home Services, Hospitality, Insurance, Legal, Logistics, Real Estate, Restaurants, Retail or General Support — each pre-configures the workflow, system prompt, voice scripts and recommended outbound triggers.",
            accent: "from-orange-500 to-pink-500",
        },
        {
            n: "3",
            title: "Embed chat &amp; attach a phone number",
            body: "Paste the FloGPT script tag before </body> for chat. Buy a phone number in-app or bring your own, then attach it to a Call Pipeline for voice. Every conversation flows into the unified CRM inbox.",
            accent: "from-cyan-500 to-blue-500",
        },
    ];

    return (
        <section
            id="flogpt-quickstart"
            aria-labelledby="quickstart-heading"
            className="relative overflow-hidden"
        >
            <div className="absolute inset-0 -z-10 opacity-30">
                <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-emerald-500/20 blur-3xl" />
                <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-cyan-500/20 blur-3xl" />
            </div>
            <div className="mx-auto max-w-6xl px-6 lg:px-8 py-20 lg:py-24">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />
                        Quickstart
                    </div>
                    <h2
                        id="quickstart-heading"
                        className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white"
                    >
                        Deploy a FloGPT AI agent in 3 steps
                    </h2>
                    <p className="mt-4 text-slate-300 leading-relaxed">
                        Most teams ship a working chat widget in under 30 minutes and a voice
                        agent end-to-end in under an hour — no engineering required.
                    </p>
                </div>
                <ol className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {steps.map((s) => (
                        <li
                            key={s.n}
                            className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6 shadow-lg shadow-black/10"
                        >
                            <div
                                className={`inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br ${s.accent} text-white font-bold shadow-lg`}
                                aria-hidden="true"
                            >
                                {s.n}
                            </div>
                            <h3
                                className="mt-4 text-lg font-semibold text-white"
                                dangerouslySetInnerHTML={{ __html: s.title }}
                            />
                            <p className="mt-2 text-sm text-slate-300 leading-relaxed">{s.body}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    );
};

/**
 * Comparison table — the row order matches `buildHomeComparisonItemListSchema`
 * so the visible table and the ItemList JSON-LD describe the same eight
 * capabilities. AI engines surface comparison tables verbatim for "X vs Y"
 * queries; we frame this as FloGPT vs. a generic chat-only AI tool to stay
 * factual without naming individual competitors in the schema body.
 */
const ComparisonSection = () => {
    const rows: [string, string, string][] = [
        ["AI chat widget for any website", "Yes", "Yes"],
        ["AI voice agent on inbound phone calls", "Yes", "No"],
        ["Outbound AI calls (webhooks, Shopify, scheduled)", "Yes", "No"],
        ["Unified CRM inbox across 7 channels", "Yes", "Partial"],
        ["Cross-channel contact identity resolution", "Yes", "No"],
        ["Visual workflow + call pipeline editors", "Yes", "Partial"],
        ["Knowledge-base-grounded answers with citations", "Yes", "Partial"],
        ["16 voice languages incl. 9 Indic locales", "Yes", "No"],
        ["Built-in ticket SLA &amp; supervisor analytics", "Yes", "No"],
        ["Free plan ($0, 100 messages/month)", "Yes", "Varies"],
    ];

    const cellTone = (v: string): string => {
        if (v === "Yes") return "text-emerald-300";
        if (v === "No") return "text-rose-300";
        if (v === "Partial") return "text-yellow-300";
        return "text-slate-300";
    };

    return (
        <section
            id="flogpt-vs-alternatives"
            aria-labelledby="comparison-heading"
            className="relative overflow-hidden"
        >
            <div className="mx-auto max-w-5xl px-6 lg:px-8 py-20 lg:py-24">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/60 bg-slate-800/40 px-3 py-1 text-xs font-medium text-slate-300">
                        <span className="h-1.5 w-1.5 rounded-full bg-gradient-to-r from-orange-500 to-pink-500" />
                        Compare
                    </div>
                    <h2
                        id="comparison-heading"
                        className="mt-5 text-3xl sm:text-4xl font-bold tracking-tight text-white"
                    >
                        FloGPT vs. a typical chat-only AI platform
                    </h2>
                    <p className="mt-4 text-slate-300 leading-relaxed">
                        Most "AI chatbot" tools stop at the website widget. FloGPT covers chat,
                        voice, the omnichannel inbox, and the trigger engine that fires outbound
                        calls — under one platform with one knowledge base.
                    </p>
                </div>
                <div className="mt-10 overflow-x-auto rounded-2xl border border-slate-800 bg-slate-900/60 shadow-xl shadow-black/10">
                    <table className="w-full text-left text-sm">
                        <caption className="sr-only">
                            Capability comparison: FloGPT versus a typical chat-only AI platform
                        </caption>
                        <thead className="bg-slate-900/80 text-slate-200">
                            <tr>
                                <th scope="col" className="px-5 py-4 font-semibold">
                                    Capability
                                </th>
                                <th scope="col" className="px-5 py-4 font-semibold text-center">
                                    FloGPT
                                </th>
                                <th scope="col" className="px-5 py-4 font-semibold text-center">
                                    Typical chat-only AI
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800">
                            {rows.map(([cap, flo, other]) => (
                                <tr key={cap} className="text-slate-300">
                                    <th
                                        scope="row"
                                        className="px-5 py-3 font-medium text-white align-top"
                                        dangerouslySetInnerHTML={{ __html: cap }}
                                    />
                                    <td
                                        className={`px-5 py-3 text-center font-semibold ${cellTone(
                                            flo
                                        )}`}
                                    >
                                        {flo}
                                    </td>
                                    <td
                                        className={`px-5 py-3 text-center font-semibold ${cellTone(
                                            other
                                        )}`}
                                    >
                                        {other}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
};

// Renders children only when scrolled near viewport (improves initial load)
const LazyMount = ({ children, minHeight = 200 }: { children: React.ReactNode; minHeight?: number }) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        // Always mount on small screens for reliability
        if (typeof window !== "undefined" && window.innerWidth < 768) {
            setVisible(true);
            return;
        }
        const element = rootRef.current;
        if (!element || visible) return;
        if (typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry.isIntersecting) {
                    setVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: "600px 0px" }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [visible]);

    return (
        <div ref={rootRef} style={{ minHeight }}>
            {visible ? (
                children
            ) : (
                <div
                    style={{ minHeight }}
                    className="w-full rounded-2xl border border-slate-800 bg-slate-900/40 animate-pulse"
                />
            )}
        </div>
    );
};

// Top-level marketing FAQ. These are deliberately platform-wide (not
// per-product) — they answer the questions a first-time visitor types into
// Google or asks an AI engine before they know which FloGPT product they
// need. Question order is deliberate: AI engines (especially Perplexity
// and Google's AI Overview) weigh the first 3-5 entries most heavily, so
// "what is FloGPT", "how is it different", and "what does it cost" sit on
// top.
const ManagedSupportSection = () => {
    const { agentPrice, supervisorPrice, billingSummary } = useManagedSupportPricing();
    const agent = agentPrice ?? "—";
    const supervisor = supervisorPrice ?? "—";

    return (
    <section id="managed-support" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30">
            <div className="pointer-events-none absolute -left-40 top-10 h-72 w-72 rounded-full bg-orange-500/30 blur-3xl" />
            <div className="pointer-events-none absolute -right-40 bottom-10 h-72 w-72 rounded-full bg-amber-500/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-7xl px-6 lg:px-8 py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                >
                    <SectionHeading
                        eyebrow="Managed Customer Support · Worldwide"
                        title="Don't want to hire a support team? We'll staff it for you"
                        description="Get trained human agents and supervisors who work directly inside your FloGPT inbox — hired, trained, and managed by us. Perfect for teams that want AI plus a human safety net without the overhead of recruiting."
                    />
                    <ul className="mt-8 space-y-3 text-slate-300">
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Support agents — {agent}/month each</strong>, handling live chat, tickets, and email.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Supervisors — {supervisor}/month each</strong>, overseeing quality and escalations.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Request in minutes</strong> — pick your seats, pay, book a kickoff call.</span></li>
                        <li className="flex items-start gap-3"><CheckIcon /><span><strong>Works with your AI</strong> — managed staff pick up exactly where the AI agent hands off.</span></li>
                    </ul>
                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <Link
                            to="/managed-support"
                            className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
                        >
                            Explore Managed Support
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                            </svg>
                        </Link>
                        <span className="text-xs text-slate-400">Available worldwide · {billingSummary}</span>
                    </div>
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, scale: 0.98 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, amount: 0.3 }}
                    className="relative"
                >
                    <div className="absolute -inset-2 rounded-2xl bg-gradient-to-r from-orange-500/20 to-pink-500/20 blur-xl" />
                    <div className="relative rounded-2xl border border-slate-800 bg-slate-900/60 p-6">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-5">
                                <div className="text-xs uppercase tracking-wider text-slate-400">Support Agent</div>
                                <div className="mt-2 text-3xl font-bold text-white">{agent}<span className="text-sm font-normal text-slate-400">/mo</span></div>
                                <div className="mt-2 text-sm text-slate-400">Live chat · tickets · email</div>
                            </div>
                            <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-5">
                                <div className="text-xs uppercase tracking-wider text-slate-400">Supervisor</div>
                                <div className="mt-2 text-3xl font-bold text-white">{supervisor}<span className="text-sm font-normal text-slate-400">/mo</span></div>
                                <div className="mt-2 text-sm text-slate-400">Quality · escalations · oversight</div>
                            </div>
                        </div>
                        <div className="mt-4 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm text-slate-300">
                            <div className="flex items-center gap-2 text-emerald-300">
                                <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                                Staffed &amp; managed by FloGPT
                            </div>
                            <p className="mt-2 text-slate-400">Cancel anytime. Scale seats up or down as your volume changes.</p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
    );
};

const HOMEPAGE_FAQS: FaqItem[] = [
    {
        question: "What is FloGPT?",
        answer: "FloGPT is an AI Chat & Voice Agents platform with a built-in AI CRM. Companies use FloGPT to embed an AI chat widget on any website, run AI voice agents that answer and place phone calls 24/7, and unify chat, voice, email, SMS, WhatsApp, Instagram and Facebook into one ticket inbox — all grounded in their own knowledge base.",
    },
    {
        question: "How is FloGPT different from ChatGPT or other chatbots?",
        answer: "ChatGPT is a general-purpose assistant. FloGPT is an end-to-end platform for deploying AI agents on your website and phone number, grounded in your business data. It includes the chat widget, voice runtime, knowledge base, workflow builder, outbound trigger engine and a unified omnichannel CRM — all in one product, with one knowledge base powering every channel.",
    },
    {
        question: "How much does FloGPT cost?",
        answer: "FloGPT has a free plan at $0 with 100 messages a month. Paid plans start at $49/month and add AI voice agents, outbound trigger campaigns, and higher message and minute caps; voice and outbound require a paid plan because they incur live telephony costs. Annual billing saves up to 20% over monthly.",
    },
    {
        question: "Do I need to write code to use FloGPT?",
        answer: "No. The chat widget installs with a single script tag (or an NPM package for React / Next.js apps). Voice agents are configured visually in the Call Pipeline editor. Workflows, knowledge base ingestion, and outbound triggers all use drag-and-drop interfaces — no engineering team required.",
    },
    {
        question: "How quickly can I get a FloGPT agent live?",
        answer: "Most teams ship a working chat widget in under 30 minutes — pick a vertical template, connect your website as a knowledge source, paste the script tag. Voice agents take under an hour end-to-end, including phone number setup and Call Pipeline customisation.",
    },
    {
        question: "Which languages do FloGPT agents support?",
        answer: "Chat agents support 50+ languages with automatic detection. Voice agents support 16 languages today: English (US, UK, IN, AU, CA, IE, NZ), Hindi, Tamil, Telugu, Kannada, Malayalam, Marathi, Bengali, Gujarati, and Punjabi. The agent can switch languages mid-conversation if the customer does.",
    },
    {
        question: "Does FloGPT include a CRM or do I still need Zendesk or Intercom?",
        answer: "FloGPT includes a built-in AI CRM. Every chat, voice call, email, SMS, WhatsApp, Instagram and Facebook conversation handled by the AI agent turns into a unified ticket — with identity resolution, AI-to-human handover, status and SLA tracking, and role-scoped analytics. You don't need a separate helpdesk to manage escalations.",
    },
    {
        question: "Do you offer managed or outsourced human support agents?",
        answer: "Yes. Managed Customer Support is available worldwide and billed in USD via Stripe or INR via Razorpay. We provide trained human agents and supervisors who work directly inside your FloGPT support inbox alongside your AI agents. Request the seats you need from your dashboard and schedule a kickoff meeting — we handle hiring, training, and staffing so you don't have to.",
    },
    {
        question: "How does FloGPT resolve the same customer across channels?",
        answer: "FloGPT identifies contacts by email, phone, WhatsApp number, Instagram handle, or Shopify customer ID and merges duplicates automatically. A customer who emailed yesterday and called today shows up as one contact with one unified timeline of every conversation, ticket, and order across every channel.",
    },
    {
        question: "Can FloGPT make outbound calls — for sales, support, or recovery?",
        answer: "Yes. Outbound AI calls fire from webhooks, Shopify events (abandoned cart, fulfilment, refund), scheduled call lists, time-relative cadences (e.g. 24 hours after a missed appointment), and internal CRM events. Per-region quiet hours, deduplication windows, India DLT compliance and TCPA consent capture are built in.",
    },
    {
        question: "Does FloGPT integrate with Shopify?",
        answer: "Yes. Install the FloGPT app from the Shopify App Store and the chat widget appears on your storefront automatically. The AI agent can look up orders, track shipments, search the catalogue, and trigger outbound recovery calls on cart-abandoned, fulfilment, refund, or any custom Admin event. Refunds and cancellations route to a human ticket with full context.",
    },
    {
        question: "What integrations does FloGPT support?",
        answer: "Managed numbers and your own Twilio account for global telephony, Jio and MSG91 for India (DLT-compliant), Google Calendar and Outlook for booking, Shopify for ecommerce, plus any HTTP API via the workflow builder's HTTP Call node and Tool registration. Outbound webhooks let you sync events to your existing CRM in real time.",
    },
    {
        question: "Is FloGPT GDPR, DPDP and SOC 2 compliant?",
        answer: "FloGPT supports region-locked data residency, opt-in PII redaction on chat and call transcripts (email, phone, credit card, SSN, IP, street address), audit logs, and SOC 2-aligned controls. Conversations can be encrypted at rest and configured for retention windows that match GDPR, India DPDP, and HIPAA requirements. Shopify GDPR data-request and redact webhooks are processed automatically.",
    },
    {
        question: "Can FloGPT replace Intercom Fin or Zendesk AI?",
        answer: "Yes for many teams. Intercom and Zendesk bolt AI on top of a ticketing tool; FloGPT is the reverse — the AI agent is the core, and the CRM is the human handover surface. You get unified chat, voice and messaging tickets, contact identity resolution, and analytics without paying separately for an AI add-on, a phone product, and a CRM seat.",
    },
];

export default function LandingPage() {
    // The shell in index.html is noindex and has no canonical. This opts the
    // homepage back into the index and pins its own URL before prerender.
    usePageSeo({
        title: "FloGPT — AI Chat & Voice Agents Platform with built-in AI CRM",
        description:
            "FloGPT is an AI Chat & Voice Agents platform with a built-in AI CRM. Embed an AI chat widget on any website, run AI phone agents 24/7, and unify chat, voice, email, SMS, WhatsApp, Instagram and Facebook into one inbox — grounded in your own knowledge base.",
        keywords:
            "AI chatbot, AI chat widget, AI voice agent, AI phone agent, conversational AI platform, AI CRM, omnichannel inbox, knowledge base AI, AI customer support, outbound AI calls, Shopify AI customer support, ChatGPT alternative for business",
        canonical: "/",
    });

    // Memoise so the JSON-LD payload is stable across re-renders (the
    // `<JsonLd>` component uses a deep-compare on `data`).
    const orgSchema = useMemo(() => buildOrganizationSchema(), []);
    const websiteSchema = useMemo(() => buildWebSiteSchema(), []);
    const appSchema = useMemo(() => buildSoftwareApplicationSchema(), []);
    const webPageSchema = useMemo(() => buildHomePageWebPageSchema(), []);
    const howToSchema = useMemo(() => buildHomeQuickStartHowToSchema(), []);
    const breadcrumbSchema = useMemo(() => buildHomeBreadcrumbSchema(), []);
    const comparisonSchema = useMemo(() => buildHomeComparisonItemListSchema(), []);

    return (
        <div className="bg-slate-900 min-h-screen overflow-x-hidden">
            <LandingNavbar />

            {/* Main Content */}
            <main>
                <Hero />
                <WhatIsFloGPTSection />
                <VideoShowcaseSection />
                {/* <section id="features">
                    <Features />
                </section> */}
                <KnowledgeBaseSection />
                <WorkflowBuilderSection />
                <ChatWidgetSection />
                <VoiceAgentsSection />
                <CRMSection />
                <ManagedSupportSection />
                <QuickStartSection />
                <ComparisonSection />
                <section id="how-it-works">
                    <Suspense fallback={<div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 text-slate-400">Loading…</div>}>
                        <LazyMount>
                            <HowItWorks />
                        </LazyMount>
                    </Suspense>
                </section>
                {/* <section id="demo">
                        <Demo />
                    </section> */}
                <section id="use-cases">
                    <Suspense fallback={<div className="mx-auto max-w-7xl px-6 lg:px-8 py-16 text-slate-400">Loading…</div>}>
                        <LazyMount>
                            <UseCases />
                        </LazyMount>
                    </Suspense>
                </section>
                <FAQSection
                    id="homepage"
                    eyebrow="FAQ"
                    heading="Frequently asked questions"
                    intro="Everything you need to know about deploying AI chat and voice agents with FloGPT."
                    items={HOMEPAGE_FAQS}
                />
            </main>

            <Footer />

            {/* Schema.org structured data. Stack design:
                - Organization + WebSite anchor the FloGPT brand entity so
                  Google's knowledge panel and AI overviews resolve "FloGPT"
                  to one canonical record.
                - WebPage links the homepage to that entity (mainEntityOfPage)
                  and marks the answer-first / TL;DR / quickstart blocks as
                  `speakable`, which AI engines use to extract clean prose
                  for voice answers and "what is X" overviews.
                - SoftwareApplication describes the product itself for
                  SaaS rich-results.
                - HowTo describes the on-page 3-step quickstart so AI
                  overviews surface it as a numbered list when answering
                  "how to deploy / set up / install" queries.
                - BreadcrumbList anchors the page in the site hierarchy
                  even though the homepage is the root.
                - ItemList describes the comparison table so engines can
                  extract the FloGPT-vs-alternative rows directly.
                - FAQPage (emitted inside `<FAQSection>`) feeds AI-engine
                  answer extraction. */}
            <JsonLd id="organization" data={orgSchema} />
            <JsonLd id="website" data={websiteSchema} />
            <JsonLd id="webpage" data={webPageSchema} />
            <JsonLd id="software-application" data={appSchema} />
            <JsonLd id="howto-quickstart" data={howToSchema} />
            <JsonLd id="breadcrumb-home" data={breadcrumbSchema} />
            <JsonLd id="comparison-itemlist" data={comparisonSchema} />
        </div>
    );
}
